import React from 'react';
import { Activity, CheckSquare, XCircle, Send, Clock, User, Building2 } from 'lucide-react';
import { getCompanyFromEmail } from '../dummyData';
import { formatNumber } from '../utils/formatUtils';

const ProjectActivity = ({ entries = [], users = [] }) => {
  // Sort entries: most recent activity at the top
  // entries are appended chronologically, so reverse gives us newest at the top
  const sortedEntries = [...entries].reverse();

  const getActivityConfig = (status) => {
    switch (status) {
      case 'Approved':
        return {
          icon: <CheckSquare className="w-5 h-5" />,
          color: 'bg-emerald-100 text-emerald-600',
          ring: 'ring-emerald-50',
          actionText: 'approved timesheet for',
          statusLabel: 'Approved'
        };
      case 'Rejected':
        return {
          icon: <XCircle className="w-5 h-5" />,
          color: 'bg-rose-100 text-rose-600',
          ring: 'ring-rose-50',
          actionText: 'rejected timesheet for',
          statusLabel: 'Rejected'
        };
      case 'Pending':
      default:
        return {
          icon: <Send className="w-5 h-5" />,
          color: 'bg-indigo-100 text-indigo-600',
          ring: 'ring-indigo-50',
          actionText: 'submitted hours for',
          statusLabel: 'Submitted'
        };
    }
  };

  return (
    <div className="max-w-5xl mx-auto h-full flex flex-col">
      <div className="mb-4 flex flex-col md:flex-row md:items-end md:justify-between gap-2">
        <div>
          <h1 className="text-2xl font-black text-slate-900 tracking-tight">Project Activity feed</h1>
          <p className="text-slate-500 text-xs font-medium mt-1 flex items-center gap-2">
            <Activity className="w-3 h-3 text-indigo-500" />
            Audit trail of WBS entries and manager authorizations.
          </p>
        </div>
        <div className="bg-white border border-slate-200 px-3 py-1 rounded-lg shadow-sm text-[10px] font-bold text-slate-600 sm:self-start">
          Total Interactions: <span className="text-indigo-600">{formatNumber(entries.length)}</span>
        </div>
      </div>

      {entries.length === 0 ? (
        <div className="bg-white rounded-xl border-2 border-dashed border-slate-200 p-8 text-center">
          <div className="bg-slate-50 w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3">
            <Clock className="w-6 h-6 text-slate-300" />
          </div>
          <h3 className="text-base font-bold text-slate-800">No activity recorded yet</h3>
          <p className="text-slate-500 text-xs max-w-xs mx-auto mt-1">Activity will appear here as team members submit hours.</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-md border border-slate-200 overflow-hidden flex-1 overflow-y-auto">
          <div className="relative border-l border-slate-100 ml-6 my-4 space-y-3">
            {sortedEntries.map((item) => {
              const user = users.find(u => u.id === item.userId);
              const config = getActivityConfig(item.status);
              const company = user ? getCompanyFromEmail(user.email) : 'Unknown';

              return (
                <div key={item.id} className="relative pl-8 pr-4">
                  {/* Timeline Node */}
                  <span className={`absolute -left-[13px] top-1 p-1.5 rounded-full ring-4 ring-white shadow-sm transition-transform hover:scale-110 ${config.color} ${config.ring}`}>
                    {React.cloneElement(config.icon, { className: 'w-3.5 h-3.5' })}
                  </span>

                  <div className="bg-slate-50/50 border border-slate-100 rounded-xl p-3 hover:bg-slate-50 transition-colors shadow-sm">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                      <div className="flex items-center gap-2">
                        <div className="w-7 h-7 rounded-full bg-white border border-slate-200 flex items-center justify-center text-slate-400">
                          <User className="w-4 h-4" />
                        </div>
                        <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2">
                          <p className="text-sm font-black text-slate-900 leading-none">
                            {user?.name || 'Unknown User'}
                          </p>
                          <span className="text-slate-300 hidden sm:inline">/</span>
                          <p className="text-[9px] uppercase font-bold text-slate-400 tracking-wider flex items-center gap-1">
                            {company}
                          </p>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-2">
                        <span className={`px-2 py-0.5 rounded-md text-[9px] font-black uppercase tracking-tighter ${config.color}`}>
                          {config.statusLabel}
                        </span>
                        <span className="text-[9px] font-bold text-slate-400 uppercase tracking-widest flex items-center gap-1">
                          {item.date}
                        </span>
                      </div>
                    </div>

                    <div className="mt-2 pl-9 flex items-center flex-wrap gap-x-2 text-xs leading-none">
                      <span className="text-slate-500 font-medium italic">{config.actionText}</span>
                      <span className="font-bold text-slate-800 px-1.5 py-0.5 bg-indigo-50 text-indigo-700 rounded-md">
                        {formatNumber(item.hours)} hours
                      </span>
                      <span className="font-black text-slate-900">
                         {item.wbsCode}
                      </span>
                      {item.status === 'Approved' && (
                        <div className="flex items-center gap-1 text-[9px] font-black text-emerald-600 uppercase tracking-widest ml-auto border-l border-slate-200 pl-2">
                          <CheckSquare className="w-3 h-3" /> Verified
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default ProjectActivity;
